// ┌──────────────────────────────────────────────┐
// │ Cart — Remove In Place (E20-1453)            │
// └──────────────────────────────────────────────┘
// Owns every Remove click in the cart, on both surfaces. Impulse ships Remove as a plain
// anchor to /cart/change?quantity=0, so clicking it navigated the shopper to /cart — which
// in the drawer meant losing whatever page they were on. This intercepts the click and
// removes through /cart/change.js instead, leaving the anchor's href as the no-JS fallback.
//
// One listener handles every line type, including the protection-plan bundle cascade, so
// there is a single place to look when a new cart line type is added. It used to be split:
// bfx-cart-protection-plan.js intercepted bundled machines and everything else navigated.
// Two document-level listeners on the same selector would both fire on every click —
// preventDefault doesn't stop a sibling listener — so merging them is what keeps a bundled
// machine from being removed twice.
//
// It also owns window.bfxCart, the small namespace the cart scripts share: the /cart.js and
// /cart/change.js helpers, the rebuild coordinator that keeps a re-render asked for here from
// racing one asked for by the plan reconcile, and the bundle id every machine/plan pairing uses.
//
// Why change.js and not update.js, and why a rendered key is never used unchecked: a line key
// is recomputed whenever the line's properties or DISCOUNT allocations change, so the key in
// the DOM can be stale before the shopper clicks. update.js ADDS a line when its key no
// longer matches (this is what caused an earlier bundle-duplication bug); change.js only
// ever changes a line that already exists. Every removal below re-reads /cart.js first and
// resolves the line against it — by the rendered key if that key is still present, otherwise
// by stable identity (variant id, bundle id, plan-or-not) — then acts on its current key.

{
  const root = () => window.Shopify?.routes?.root || '/';

  // Rejects a non-OK response rather than parsing it. An error payload is still valid JSON, and
  // handing it back as a cart fails quietly in both callers: the reconcile's shape guards read
  // a body with no `items` as "nothing to reconcile" and stop, and removal only fails later,
  // indirectly. Throwing here puts every shared caller into its own error handling.
  const getCart = () =>
    fetch(`${root()}cart.js`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((r) => {
      if (!r.ok) throw new Error(`cart.js failed (${r.status})`);
      return r.json();
    });

  // change.js changes a single existing line by its (current) key. Never adds. Properties, when
  // given, REPLACE the line's whole set, so pass the ones to keep along with the new ones.
  // Quantity is always sent: a properties-only change resets the line to 1.
  const changeLine = (key, quantity, properties) =>
    fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(properties ? { id: key, quantity, properties } : { id: key, quantity }),
    });

  // v4 UUID for a machine/plan pairing's _bundle_id — unique per order, which is all the
  // integration (Axian → Bipsli → SAP) needs to match each plan to its machine. This runs on the
  // purchase path, so it must never throw: prefer crypto.randomUUID, fall back to
  // crypto.getRandomValues for older browsers (e.g. Safari < 15.4), and if crypto is missing or
  // blocked entirely (locked-down / non-secure context), drop to a Math.random v4-shaped id. The
  // whole crypto lookup sits inside the try so anything unexpected falls through instead.
  const bundleId = () => {
    try {
      const cryptoObj = globalThis.crypto;
      if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
      if (cryptoObj?.getRandomValues) {
        const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
        bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
        const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
      }
    } catch {
      // fall through to the non-crypto fallback
    }
    // Last resort — not cryptographically strong, but the id only needs to be unique per order,
    // and a valid v4 shape keeps the payload contract intact.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const rand = (Math.random() * 16) | 0;
      return (char === 'x' ? rand : (rand & 0x3) | 0x8).toString(16);
    });
  };

  // Rebuild coordination.
  //
  // Impulse rebuilds the cart by fetching the ajax cart view and applying whatever comes back:
  // buildCart() never awaits its fetch and cartMarkup() never checks that the response is still
  // the newest, so two overlapping builds land last-response-wins and an older one can repaint
  // the cart with lines that are already gone.
  //
  // Two guards, because builds come from two places. requestBuild() owns every build our own
  // cart scripts ask for and runs one at a time. The freshness wrap covers the ones the theme
  // starts by itself, which we never see: a quantity change calls buildCart() directly and then
  // fires cart:updated, which starts the plan reconcile, whose rebuild would otherwise overlap
  // the one already in flight. theme.js is vendor code we don't edit, so the wrap goes around it
  // from here.

  // How long a build may take to land, for both the queue and the focus restore below. Long
  // enough not to trip on a slow cart render; short enough that a failed fetch, which lands
  // nothing at all, can't wedge either of them for the rest of the page's life.
  const BUILD_TIMEOUT = 3000;

  // Resolves true once a build has landed, false if nothing arrives within BUILD_TIMEOUT. The
  // theme empties and refills [data-products] rather than replacing it, so a childList mutation on
  // one is the signal.
  //
  // Every root is observed because only one of them is ever live, and which one depends on the
  // page: Impulse skips CartDrawer entirely on the cart template, so there the drawer's
  // [data-products] is rendered but inert and the cart page's is the one that fills. Watching all
  // of them means not having to know which surface this build belongs to; the inert ones never
  // mutate, so they can't signal early.
  //
  // The signal is "a build landed", not "the build we asked for landed" — a mutation from a build
  // the coordinator never started (the theme's own, off a quantity change) releases the queue too,
  // and a queued request can then go out beside that one. The freshness wrap below is what keeps
  // the cart correct when that happens; this signal cannot tell the two apart on its own.
  //
  // Nothing to observe means nothing to wait for, so an empty page resolves true rather than
  // sitting out the timeout and reporting a failure that never happened.
  const buildLanded = () =>
    new Promise((resolve) => {
      const roots = document.querySelectorAll('[data-products]');
      if (!roots.length) {
        resolve(true);
        return;
      }

      let timer = null;
      const finish = (landed) => {
        clearTimeout(timer);
        observer.disconnect();
        resolve(landed);
      };

      const observer = new MutationObserver(() => finish(true));
      roots.forEach((productsRoot) => observer.observe(productsRoot, { childList: true }));
      timer = setTimeout(() => finish(false), BUILD_TIMEOUT);
    });

  let building = false;
  let trailingBuild = null;

  const dispatchBuild = () => {
    building = true;
    document.dispatchEvent(new CustomEvent('cart:build'));
    return buildLanded().then((landed) => {
      building = false;
      const queued = trailingBuild;
      trailingBuild = null;
      // Resolving with the promise chains them, so everyone who queued behind this build settles
      // on the trailing build's outcome rather than on this one's.
      if (queued) queued.release(dispatchBuild());
      return landed;
    });
  };

  // Ask the cart to re-render, and hand back a promise that resolves true when the build THIS
  // caller asked for has landed, false if it never did.
  //
  // A request made while a build is in flight flags exactly one trailing rebuild rather than
  // racing the one already running — the trailing fetch then starts after the first has landed, so
  // it can only carry newer state. Every caller that queued during that window shares the one
  // trailing build, so they share its promise too.
  //
  // The promise is what lets a caller time its own recovery off the build rather than off the
  // click: the dispatch can be held here for as long as an in-flight build takes, and a countdown
  // started before the call would expire just as the deferred build was released.
  const requestBuild = () => {
    if (!building) return dispatchBuild();

    if (!trailingBuild) {
      let release;
      const landed = new Promise((resolve) => {
        release = resolve;
      });
      trailingBuild = { landed, release };
    }

    return trailingBuild.landed;
  };

  // Make the theme apply only the newest markup it asked for.
  //
  // Superseded requests resolve with the newest response instead of their own, rather than being
  // left unsettled: buildCart() chains .then(cartMarkup) onto every call it makes, so a promise
  // that never resolves leaves that chain pending for the life of the page. Handing back the
  // newest markup means every repaint the theme performs is a correct one — and it is the same
  // markup the superseded request was fetching anyway, since the URL never varies.
  //
  // The wrap is best-effort over vendor code, so a missing theme.cart opts out rather than taking
  // removal down with it — but it says so, because what is lost is the last-response-wins
  // protection this file exists for and nothing else would report it. The condition it depends on
  // is load order: theme.cart is top-level in theme.js and both tags are deferred, so document
  // order in layout/theme.liquid decides it. That order is asserted in
  // tests/theme/cart-build-coordination.test.js, since reordering the two tags would otherwise
  // retire the wrap silently.
  const applyNewestMarkupOnly = () => {
    const cart = window.theme?.cart;
    if (typeof cart?.getCartProductMarkup !== 'function') {
      console.warn(
        'bfx-cart-remove: theme.cart.getCartProductMarkup is missing, so cart rebuilds started by ' +
          'the theme are back to last-response-wins and a stale response can repaint the cart.'
      );
      return;
    }

    const fetchMarkup = cart.getCartProductMarkup.bind(cart);
    let requested = 0;
    let newest = null;

    cart.getCartProductMarkup = () => {
      const mine = (requested += 1);
      newest = fetchMarkup().then((markup) => (mine === requested ? markup : newest));
      return newest;
    };
  };

  applyNewestMarkupOnly();

  // Shared with bfx-cart-protection-plan.js, which runs the same two cart calls for its qty
  // reconcile and asks for its re-render through the same coordinator. One definition so the
  // two can't drift apart.
  // Holds a cart surface still while one of our changes is under way: every line inert (dimmed by
  // _cart.scss) except the part of the page doing the work, and checkout held so a half-changed
  // cart can't reach the order. Without it, a quantity change mid-way runs the reconcile against
  // a cart that's still being rebuilt. Returns the release; the re-render replaces the frozen
  // lines anyway, so releasing mostly matters for checkout and for a change that fails.
  const lock = (surface, keep = null, { announce = '' } = {}) => {
    if (!surface) return () => {};
    // Around `keep`, freeze at every level down to it — each ancestor's other children — so only
    // it stays live, however deep it sits (the Remove link is inside the details column, beside
    // the name and stepper, which must not stay live).
    const keepLine = keep?.closest('.cart__item');
    const around = (line) => {
      const out = [];
      // Stops where there's no element list to walk (the tests' stand-in cart has none).
      for (let node = line; node?.children && node !== keep;) {
        const children = [...node.children];
        const next = children.find((child) => child.contains?.(keep));
        out.push(...children.filter((child) => child !== next));
        node = next;
      }
      return out;
    };
    const frozen = [...(surface.querySelectorAll?.('.cart__item') ?? [])]
      .flatMap((line) => (line === keepLine ? around(line) : [line]))
      .filter((element) => !element.inert);
    frozen.forEach((element) => {
      element.inert = true;
    });
    // Only buttons this disables are re-enabled, so one something else disabled stays that way.
    const checkouts = [
      ...(surface.closest?.('form')?.querySelectorAll('[name="checkout"]') ?? []),
    ].filter((button) => !button.disabled);
    checkouts.forEach((button) => {
      button.disabled = true;
    });
    // Shop Pay and the other accelerated checkouts are Shopify's own markup, not a named button
    // this can disable, so their container goes inert instead.
    const expressCheckouts = [
      ...(surface.closest?.('form')?.querySelectorAll('.additional-checkout-buttons') ?? []),
    ].filter((container) => !container.inert);
    expressCheckouts.forEach((container) => {
      container.inert = true;
    });
    // The drawer's own way off the page too: leaving mid-change can abort the second half of it
    // (a split's reduction), and the next page then shows an extra machine with no word why. Only
    // View cart, the link beside the lines; the rest of the page can't be held this way. Not the
    // drawer's Close: closing it keeps the shopper on the page with the change running, and it is
    // where a removal that empties the drawer puts focus while this lock is still held.
    const drawerExits = [
      ...(surface.closest?.('.drawer')?.querySelectorAll('.cart__view-cart') ?? []),
    ].filter((control) => !control.inert);
    drawerExits.forEach((control) => {
      control.inert = true;
    });
    // Say why the cart has gone quiet to screen readers: the caller shows it in place (the Remove
    // link, the plan offer's Add), and a changed label isn't announced. Inserted empty, then
    // filled, so the live region exists before its words do.
    let announcer = null;
    if (announce && surface.prepend) {
      announcer = document.createElement('p');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('role', 'status');
      surface.prepend(announcer);
      requestAnimationFrame(() => {
        announcer.textContent = announce;
      });
    }

    return () => {
      announcer?.remove();
      frozen.forEach((element) => {
        element.inert = false;
      });
      checkouts.forEach((button) => {
        button.disabled = false;
      });
      expressCheckouts.forEach((container) => {
        container.inert = false;
      });
      drawerExits.forEach((control) => {
        control.inert = false;
      });
    };
  };

  window.bfxCart = { root, getCart, changeLine, requestBuild, bundleId, lock };

  // The Remove link while its removal runs: a spinner and the removing message in place of
  // "Remove". The re-render replaces the link, so nothing puts it back on success; a removal
  // that falls back to navigation leaves the page anyway.
  const showRemoving = (link, message) => {
    if (!message) return;
    const spinner = document.createElement('span');
    spinner.className = 'cart__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    const words = document.createElement('span');
    words.textContent = message;
    link.replaceChildren(spinner, words);
    link.classList.add('is-loading');
  };

  // Resolve a rendered line against a freshly fetched cart.
  //
  // The rendered key comes first, but only ever as a lookup INTO the fresh cart — if the cart
  // still holds that key it identifies the line exactly, including two lines of the same
  // variant that differ only by line-item properties (two gift-card recipients, say), which
  // the identity match below cannot tell apart. That's a check, not the trust the earlier
  // bundle-duplication bug placed in a key it never re-verified.
  //
  // When the key has been recomputed out from under us, fall back to identity. Variant id
  // alone is ambiguous when the same variant sits in the cart twice (a bare machine plus a
  // bundled one), so the bundle id and plan flag disambiguate — all three come from the
  // line's properties, which is what makes them stable where the key isn't.
  const findLine = (cart, { key, variantId, bundleId, isPlan }) =>
    (key && cart.items.find((item) => item.key === key)) ||
    cart.items.find((item) => {
      if (String(item.variant_id) !== String(variantId)) return false;
      const props = item.properties || {};
      if ((props._bundle_id || '') !== (bundleId || '')) return false;
      return Boolean(props._parent_sku) === isPlan;
    });

  // Restores focus as rebuilt markup lands, and hands back the handle that stops the watch.
  //
  // cart:build swaps out the whole line-item subtree, taking the focused Remove link with it, so
  // without this a keyboard or screen-reader user is dumped back to the top of the document after
  // every removal. [data-products] itself survives (the theme empties and refills it), which is
  // what makes it a usable anchor. Falls to the line that took the removed one's place, then the
  // last line, then the drawer's close button.
  //
  // The first swap gets focus back unconditionally — the link the shopper was on went with it.
  // Later swaps only reclaim it from body: a rebuild another script already had queued can still
  // land after the removal's own and drop the focus just placed. If the shopper has tabbed
  // somewhere deliberate by then, leave them alone.
  //
  // No clock of its own: the caller stops the watch off the build it actually asked for, so the
  // two can't drift apart when the queue defers that build behind one already in flight.
  const restoreFocusOnRebuild = (productsRoot, index, inDrawer) => {
    const placeFocus = () => {
      const links = productsRoot.querySelectorAll('.cart__remove a');
      const fallback = inDrawer ? document.querySelector('.drawer__close-button') : null;
      (links[Math.min(index, links.length - 1)] || fallback)?.focus();
    };

    let placed = false;
    const observer = new MutationObserver(() => {
      if (placed && document.activeElement !== document.body) return;
      placed = true;
      requestAnimationFrame(placeFocus);
    });
    observer.observe(productsRoot, { childList: true });

    return () => observer.disconnect();
  };

  // Every line sharing a bundle id, plans first — removing the machine first would leave
  // its plan briefly orphaned on screen before the cascade caught up.
  const bundleLines = (cart, bundleId) => {
    const lines = cart.items.filter((item) => item.properties?._bundle_id === bundleId);
    return [
      ...lines.filter((item) => item.properties?._parent_sku),
      ...lines.filter((item) => !item.properties?._parent_sku),
    ];
  };

  // Removes one line and returns the updated cart change.js hands back. Throws on a non-OK
  // response so the caller falls back to the anchor: re-rendering after a failed change
  // would show the shopper a cart they think they emptied, with no error and no fallback.
  const removeLine = async (key) => {
    const res = await changeLine(key, 0);
    if (!res.ok) throw new Error(`change.js failed (${res.status}) removing line ${key}`);
    return res.json().catch(() => null);
  };

  // One removal at a time. Each pass re-reads the cart, so two overlapping runs would race:
  // the second resolves against a cart the first is still changing, and a rapid double-click
  // could fail on a key the first run already spent and take the navigation fallback — losing
  // the shopper's place, which is the whole thing this file exists to prevent.
  let removing = false;

  // A quantity change the theme has sent but not finished marks its selector is-loading until the
  // response lands, then rebuilds the cart without waiting (theme.js, CartForm.quantityChanged).
  // The same wait the plan offer's add makes (bfx-protection-plan-cart-offer.js).
  const quantityPending = (surface) => !!surface?.querySelector('.js-qty__wrapper.is-loading');

  // Resolves true once that change's rebuild has replaced the lines, false after a few seconds
  // without it, so a lost request can't hold a removal forever.
  const quantitySettled = (surface) =>
    new Promise((resolve) => {
      let timer = null;
      const observer = new MutationObserver(() => {
        if (quantityPending(surface)) return;
        clearTimeout(timer);
        observer.disconnect();
        resolve(true);
      });
      observer.observe(surface, { childList: true });
      timer = setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, 5000);
    });

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('.cart__remove a');
    if (!link) return;

    const item = link.closest('.cart__item');
    if (!item) return;

    const { variantId, bundleId, key: renderedKey } = item.dataset;
    const isPlan = item.dataset.isPlan === 'true';
    // No variant id means markup this script doesn't understand — leave the anchor alone
    // rather than guess, so removal still works via navigation.
    if (!variantId) return;

    // A bundle's removal leaves a machine that only the plan reconcile (bfx-cart-protection-plan.js)
    // can clean, and that script loads just after this one. A press in the moment between would
    // render the machine still flagged, so let the anchor navigate instead: the page load that
    // follows runs the reconcile. Ordinary lines don't need it and stay in place.
    if (bundleId && !window.bfxCart.reconcile) return;

    // Swallow the click rather than letting it through: following the anchor mid-removal is
    // exactly the navigation we're avoiding.
    event.preventDefault();
    if (removing) return;
    removing = true;

    // Frozen from the click until the cart has re-rendered, as a plan add is: nothing else can
    // change the cart while this removal (and the reconcile it waits for) is under way. The Remove
    // link stays lit and says what's happening, as the offer's Add does ("Removing…").
    const removingMessage = link.dataset?.removingMessage || '';
    const productsRoot = link.closest('[data-products]');
    let release = lock(productsRoot, link, { announce: removingMessage });
    showRemoving(link, removingMessage);
    // Held through every re-render during the removal, as the plan add's lock is: a quantity
    // change already in flight rebuilds the cart with live lines, so the lock is taken again over
    // them as they land (a MutationObserver callback runs before any click can reach them).
    const relock = productsRoot
      ? new MutationObserver((records) => {
          const replaced = records.some((record) =>
            [...record.removedNodes].some(
              (node) => node.matches?.('.cart__item') || node.querySelector?.('.cart__item')
            )
          );
          if (!replaced) return;
          release();
          release = lock(productsRoot);
        })
      : null;
    relock?.observe(productsRoot, { childList: true });
    // Leaving the page keeps the lock, as the plan offer's reload does: releasing it would hand
    // checkout and the lines back for the rest of the unload, against a cart just declared stale.
    let leaving = false;
    const leave = (go) => {
      leaving = true;
      go();
    };

    // Captured before the rebuild destroys the line this click came from.
    const inDrawer = !!link.closest('.drawer');
    const removedIndex = productsRoot
      ? [...productsRoot.querySelectorAll('.cart__item')].indexOf(item)
      : -1;

    // Out here so the catch can tell a clean failure (nothing removed, the anchor is still a
    // valid fallback) from one that already changed the cart, and so it can fall back on the
    // key the fresh cart actually resolved rather than the one rendered into the anchor.
    let removedAny = false;
    let resolvedKey = null;

    try {
      // Let a quantity change still in flight land before reading the cart, so the removal resolves
      // its line against the cart that change leaves. A wait that times out goes ahead rather than
      // stop, unlike the add: the relock above holds through a late rebuild, and every key below is
      // resolved from a fresh read, so a late change is handled as any key that changed under a
      // removal is.
      if (quantityPending(productsRoot)) await quantitySettled(productsRoot);

      let updatedCart = null;

      if (bundleId && item.classList.contains('cart__item--has-bundled-child')) {
        const initial = await getCart();
        // Resolve the clicked machine up front, the same way the ordinary path does, so a
        // failure before anything has mutated can still fall back on a key that exists rather
        // than the rendered one this lookup may have just proved stale.
        resolvedKey = findLine(initial, { key: renderedKey, variantId, bundleId, isPlan })?.key;
        // Drop the machine and its plan together so the plan never flashes as an orphan.
        const targets = bundleLines(initial, bundleId);
        // Each change.js reshuffles the remaining keys, so every pass resolves its target against
        // the latest cart rather than a key from an earlier read. That cart is the one change.js
        // hands back — the whole cart, keys already reshuffled — so the passes don't go back out
        // to /cart.js for it (measured: 6 requests to 4, ~0.4s). The read above seeds the first.
        // Identity only here, no key.
        let current = initial;
        for (const target of targets) {
          const match = findLine(current, {
            variantId: target.variant_id,
            bundleId,
            isPlan: Boolean(target.properties?._parent_sku),
          });
          if (!match) continue;
          updatedCart = await removeLine(match.key);
          removedAny = true;
          // A response that won't parse costs one read, not the cascade.
          current = Array.isArray(updatedCart?.items) ? updatedCart : await getCart();
        }
      } else {
        const current = await getCart();
        const match = findLine(current, { key: renderedKey, variantId, bundleId, isPlan });
        if (match) {
          resolvedKey = match.key;
          updatedCart = await removeLine(match.key);
          removedAny = true;
        }
      }

      // Nothing was removed — the line had already gone (another tab, or a click that landed
      // twice). Keep the real cart rather than null so the empty-state check below still sees
      // the true count instead of skipping and leaving the page half-rebuilt.
      if (!updatedCart) updatedCart = await getCart();

      // The cart page renders its empty state AND its whole summary form server-side, gated
      // on cart.item_count (main-cart.liquid). The theme's cart:build only swaps the line
      // items inside that form, so emptying the last line in place leaves a $0.00 subtotal
      // and a Check Out button sitting on an empty cart, with the "cart is empty" line
      // missing. Reload so the server renders the real empty state — there is no place left
      // to lose by then. The drawer builds its own empty state correctly, so it never reloads
      // and the whole point of this fix survives where it matters.
      if (updatedCart?.item_count === 0 && document.body.classList.contains('template-cart')) {
        leave(() => window.location.reload());
        return;
      }

      // Armed before the rebuild is asked for, so the observer can't miss it.
      const stopRestoringFocus = productsRoot
        ? restoreFocusOnRebuild(productsRoot, removedIndex, inDrawer)
        : null;

      // Reconcile before re-rendering. Removing a plan leaves its machine carrying bundle
      // properties, which the reconcile (bfx-cart-protection-plan.js) rebuilds as a clean line —
      // and Shopify merges that into an identical clean line if the cart has one. Rendering
      // first showed the half-finished cart and then the merge, two jumps ~2s apart (measured).
      // Awaiting it here gives one render, straight to where the cart settles. Without the
      // reconcile loaded, the cart:updated below still goes out for any other listener; a bundle
      // line can't reach here then (its Remove follows the anchor until the reconcile loads).
      let reconciled = false;
      let reconcileChanged = false;
      if (window.bfxCart.reconcile) {
        try {
          reconcileChanged = await window.bfxCart.reconcile(updatedCart);
          reconciled = true;
        } catch (error) {
          console.error('Cart reconcile before re-render failed; rendering anyway:', error);
        }
      }

      // The reconcile can empty the cart itself, after the check above passed: the last line left
      // was a plan whose machine had gone, and it removed that plan. Same reason to reload.
      if (reconcileChanged && document.body.classList.contains('template-cart')) {
        const settledCart = await getCart().catch((error) => {
          console.warn('Cart re-read after reconcile failed; rendering in place:', error);
          return null;
        });
        if (settledCart?.item_count === 0) {
          leave(() => window.location.reload());
          return;
        }
      }

      // Timed off the build this removal asks for, not off the click: requestBuild() holds the
      // dispatch while another build is in flight, and a countdown started here would expire at
      // roughly the instant the deferred build was finally released.
      const settled = requestBuild();

      // Carry the cart the way the theme's CartForm does (theme.js dispatches `cart:updated`
      // with `detail.cart`), so listeners don't go back out to /cart.js for state we hold. Not
      // after a direct reconcile: the reconcile is this event's only listener, it has just run,
      // and the cart held here predates what it changed.
      if (!reconciled) {
        document.dispatchEvent(
          new CustomEvent(
            'cart:updated',
            updatedCart ? { detail: { cart: updatedCart } } : undefined
          )
        );
      }

      // Keep watching one build past this one, then stop. The reconcile has already run by now, so
      // the later swap this covers is a rebuild another script already had queued.
      if (stopRestoringFocus) {
        settled.then(() => setTimeout(stopRestoringFocus, BUILD_TIMEOUT));
      }

      // The line is gone server-side, so a rebuild that never lands leaves the shopper looking
      // at a line they already removed. Reload rather than let the stale DOM stand — the theme
      // fetches its markup unawaited, so a rejected request surfaces no other way.
      if (!(await settled)) {
        console.error('Cart rebuild did not land after removal; reloading to resync.');
        leave(() => window.location.reload());
      }
    } catch (error) {
      console.error('Cart removal failed:', error);
      // Falling back to the anchor only helps if its key still exists. A cascade that failed
      // part-way has already reshuffled the remaining keys, so show the shopper the real state
      // instead. Otherwise navigate the anchor, but with the key the fresh cart resolved — the
      // rendered one may be exactly what the lookup just proved stale.
      if (removedAny) {
        leave(() => window.location.reload());
      } else if (resolvedKey && resolvedKey !== renderedKey) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('id', resolvedKey);
        leave(() => window.location.assign(url.toString()));
      } else {
        leave(() => window.location.assign(link.href));
      }
    } finally {
      // Leaving keeps the observer on too, so a render that lands before the page goes is locked.
      // Otherwise it stops before the release, so the release is the last word on the lock.
      if (!leaving) {
        relock?.disconnect();
        release();
      }
      removing = false;
    }
  });
}
